import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/components/Toast';
import { SimpleModal } from '@/components/SimpleModal';
import { 
  ShoppingBag, Clock, CheckCircle, Truck, Package, XCircle, 
  Phone, MapPin, User, CreditCard, RefreshCw, ChevronRight,
  MessageCircle, Eye, FileText, Loader2, Receipt, DollarSign
} from 'lucide-react';

const STATUS_CONFIG = {
  pending: { label: 'Pendente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  confirmed: { label: 'Confirmado', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  preparing: { label: 'Preparando', color: 'bg-purple-100 text-purple-700', icon: Package },
  ready: { label: 'Pronto', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  delivered: { label: 'Entregue', color: 'bg-slate-100 text-slate-700', icon: Truck },
  cancelled: { label: 'Cancelado', color: 'bg-rose-100 text-rose-700', icon: XCircle }
};

const STATUS_FLOW = ['pending', 'confirmed', 'preparing', 'ready', 'delivered'];

export const PedidosOnlinePage = () => {
  const toast = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [emitindoNfce, setEmitindoNfce] = useState(null);
  const [showEmitirNotaModal, setShowEmitirNotaModal] = useState(false);
  const [selectedOrderNota, setSelectedOrderNota] = useState(null);
  const [tipoDocumento, setTipoDocumento] = useState('nfce');

  useEffect(() => {
    loadOrders();
    // Auto-refresh a cada 30 segundos
    const interval = setInterval(loadOrders, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadOrders = async () => {
    try {
      const response = await api.get('/catalogo/pedidos');
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    setUpdatingStatus(orderId);
    try {
      await api.put(`/catalogo/pedidos/${orderId}/status`, { status: newStatus });
      toast.success(`Pedido atualizado para: ${STATUS_CONFIG[newStatus].label}`);
      loadOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Erro ao atualizar status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getNextStatus = (currentStatus) => {
    const currentIndex = STATUS_FLOW.indexOf(currentStatus);
    if (currentIndex < STATUS_FLOW.length - 1) {
      return STATUS_FLOW[currentIndex + 1];
    }
    return null;
  };

  const sendWhatsApp = (order) => {
    const phone = order.customer_phone.replace(/\D/g, '');
    let message = `Olá ${order.customer_name}! `;
    
    if (order.status === 'confirmed') {
      message += `Seu pedido #${order.order_number} foi confirmado e está sendo preparado!`;
    } else if (order.status === 'ready') {
      message += `Seu pedido #${order.order_number} está pronto${order.delivery_type === 'retirada' ? ' para retirada!' : ' e saiu para entrega!'}`;
    } else if (order.status === 'delivered') {
      message += `Seu pedido #${order.order_number} foi entregue. Obrigado pela preferência!`;
    } else {
      message += `Sobre seu pedido #${order.order_number}...`;
    }
    
    window.open(`https://wa.me/55${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleEmitirNfe = async (orderId) => {
    if (!window.confirm('Deseja emitir NF-e (DANFE) para este pedido?')) return;
    
    setEmitindoNfce(orderId);
    try {
      const response = await api.post(`/catalogo/pedidos/${orderId}/emitir-nfe`);
      
      if (response.data.sucesso) {
        toast.success(`NF-e ${response.data.nfe_number} emitida com sucesso!`);
        loadOrders();
        if (selectedOrder?.id === orderId) {
          setSelectedOrder(prev => ({
            ...prev,
            nfe_key: response.data.nfe_key,
            nfe_number: response.data.nfe_number,
            nfe_status: 'autorizada'
          }));
        }
      } else {
        toast.error(response.data.mensagem || 'Erro ao emitir NF-e');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao emitir NF-e');
    } finally {
      setEmitindoNfce(null);
    }
  };

  const handleEmitirNota = async () => {
    if (!selectedOrderNota) return;
    
    setEmitindoNfce(selectedOrderNota.id);
    try {
      const response = await api.post(`/catalogo/pedidos/${selectedOrderNota.id}/emitir-nota`, {
        tipo_documento: tipoDocumento
      });
      
      const tipoNota = tipoDocumento === 'nfe' ? 'NF-e' : 'NFC-e';
      const ambienteLabel = response.data.ambiente === 'producao' ? 'Produção' : 'Homologação';
      
      if (response.data.sucesso || response.data.success) {
        toast.success(`${tipoNota} ${response.data.numero} emitida com sucesso! (${ambienteLabel})`);
        loadOrders();
        setShowEmitirNotaModal(false);
        setSelectedOrderNota(null);
        if (selectedOrder?.id === selectedOrderNota.id) {
          setSelectedOrder(prev => ({
            ...prev,
            [`${tipoDocumento}_key`]: response.data.chave,
            [`${tipoDocumento}_number`]: response.data.numero,
            [`${tipoDocumento}_status`]: 'autorizada'
          }));
        }
      } else {
        toast.error(response.data.mensagem || 'Erro ao emitir nota');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao emitir nota fiscal');
    } finally {
      setEmitindoNfce(null);
    }
  };

  const [finalizando, setFinalizando] = useState(null);

  const handleFinalizar = async (orderId) => {
    if (!window.confirm('Finalizar pedido e registrar no fluxo de caixa?')) return;
    
    setFinalizando(orderId);
    try {
      const response = await api.post(`/orders/online/${orderId}/finalize`);
      
      if (response.data.success) {
        toast.success(`Pedido finalizado! Valor: R$ ${response.data.amount?.toFixed(2) || '0,00'} registrado no caixa`);
        loadOrders();
        if (selectedOrder?.id === orderId) {
          setSelectedOrder(prev => ({
            ...prev,
            status: 'finalizado',
            cashflow_generated: true
          }));
        }
      } else {
        toast.error(response.data.error || 'Erro ao finalizar pedido');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao finalizar pedido');
    } finally {
      setFinalizando(null);
    }
  };

  const filteredOrders = filterStatus === 'all' 
    ? orders 
    : orders.filter(o => o.status === filterStatus);

  const pendingCount = orders.filter(o => o.status === 'pending').length;

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Pedidos Online
          </h1>
          <p className="text-slate-500">Gerencie os pedidos recebidos pelo catálogo</p>
        </div>
        <div className="flex items-center gap-3">
          {pendingCount > 0 && (
            <Badge className="bg-amber-500 text-white px-3 py-1">
              {pendingCount} novo(s)
            </Badge>
          )}
          <Button variant="outline" onClick={loadOrders}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <Button
          variant={filterStatus === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterStatus('all')}
          className={filterStatus === 'all' ? 'bg-slate-900' : ''}
        >
          Todos ({orders.length})
        </Button>
        {Object.entries(STATUS_CONFIG).map(([key, config]) => {
          const count = orders.filter(o => o.status === key).length;
          if (count === 0 && key !== 'pending') return null;
          return (
            <Button
              key={key}
              variant={filterStatus === key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus(key)}
              className={filterStatus === key ? 'bg-slate-900' : ''}
            >
              {config.label} ({count})
            </Button>
          );
        })}
      </div>

      {/* Lista de Pedidos */}
      {filteredOrders.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <ShoppingBag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Nenhum pedido encontrado</h3>
            <p className="text-slate-500">Os pedidos do catálogo aparecerão aqui</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredOrders.map((order) => {
            const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
            const StatusIcon = statusConfig.icon;
            const nextStatus = getNextStatus(order.status);
            
            return (
              <Card key={order.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-bold text-lg">#{order.order_number}</span>
                        <Badge className={statusConfig.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                        {order.delivery_type === 'entrega' && (
                          <Badge variant="outline" className="border-blue-300 text-blue-600">
                            <Truck className="w-3 h-3 mr-1" />
                            Entrega
                          </Badge>
                        )}
                        {order.nfe_key ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <FileText className="w-3 h-3 mr-1" />
                            NF-e {order.nfe_number}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-slate-300 text-slate-500">
                            <FileText className="w-3 h-3 mr-1" />
                            Sem NF-e
                          </Badge>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center gap-2 text-slate-600">
                          <User className="w-4 h-4" />
                          <span>{order.customer_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Phone className="w-4 h-4" />
                          <span>{order.customer_phone}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <CreditCard className="w-4 h-4" />
                          <span className="capitalize">{order.payment_method}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Clock className="w-4 h-4" />
                          <span>{new Date(order.created_at).toLocaleString('pt-BR')}</span>
                        </div>
                      </div>
                      
                      {order.delivery_type === 'entrega' && order.delivery_address && (
                        <div className="flex items-start gap-2 mt-2 text-sm text-slate-600">
                          <MapPin className="w-4 h-4 mt-0.5" />
                          <span>{order.delivery_address}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="text-right ml-4">
                      <p className="text-2xl font-bold text-emerald-600">
                        {formatCurrency(order.total)}
                      </p>
                      <p className="text-sm text-slate-500">
                        {order.items?.length || 0} item(ns)
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedOrder(order)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Detalhes
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => sendWhatsApp(order)}
                        className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                      >
                        <MessageCircle className="w-4 h-4 mr-1" />
                        WhatsApp
                      </Button>
                      {!order.nfe_key && !order.nfce_key && order.status !== 'cancelled' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedOrderNota(order);
                              setTipoDocumento('nfce');
                              setShowEmitirNotaModal(true);
                            }}
                            disabled={emitindoNfce === order.id}
                            className="text-green-600 border-green-300 hover:bg-green-50"
                          >
                            {emitindoNfce === order.id ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Receipt className="w-4 h-4 mr-1" />
                            )}
                            NFC-e
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedOrderNota(order);
                              setTipoDocumento('nfe');
                              setShowEmitirNotaModal(true);
                            }}
                            disabled={emitindoNfce === order.id}
                            className="text-blue-600 border-blue-300 hover:bg-blue-50"
                          >
                            {emitindoNfce === order.id ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <FileText className="w-4 h-4 mr-1" />
                            )}
                            NF-e
                          </Button>
                        </>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      {order.status !== 'cancelled' && order.status !== 'delivered' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-rose-600 border-rose-300 hover:bg-rose-50"
                          onClick={() => updateStatus(order.id, 'cancelled')}
                          disabled={updatingStatus === order.id}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Cancelar
                        </Button>
                      )}
                      {nextStatus && (
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => updateStatus(order.id, nextStatus)}
                          disabled={updatingStatus === order.id}
                        >
                          {updatingStatus === order.id ? (
                            <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <ChevronRight className="w-4 h-4 mr-1" />
                          )}
                          {STATUS_CONFIG[nextStatus].label}
                        </Button>
                      )}
                      {order.status === 'delivered' && !order.cashflow_generated && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleFinalizar(order.id)}
                          disabled={finalizando === order.id}
                          data-testid={`finalize-btn-${order.id}`}
                        >
                          {finalizando === order.id ? (
                            <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-1" />
                          )}
                          Finalizar e Registrar Caixa
                        </Button>
                      )}
                      {order.cashflow_generated && (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          No Caixa
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal de Detalhes */}
      <SimpleModal
        open={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        title={`Pedido #${selectedOrder?.order_number}`}
        maxWidth="max-w-2xl"
      >
        {selectedOrder && (
          <div className="space-y-4">
            {/* Status */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge className={STATUS_CONFIG[selectedOrder.status]?.color || 'bg-slate-100'}>
                  {STATUS_CONFIG[selectedOrder.status]?.label || selectedOrder.status}
                </Badge>
                {selectedOrder.nfe_key ? (
                  <Badge className="bg-emerald-100 text-emerald-700">
                    <FileText className="w-3 h-3 mr-1" />
                    NF-e {selectedOrder.nfe_number}
                  </Badge>
                ) : null}
              </div>
              <span className="text-sm text-slate-500">
                {new Date(selectedOrder.created_at).toLocaleString('pt-BR')}
              </span>
            </div>
            
            {/* NF-e Info */}
            {selectedOrder.nfe_key && (
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-emerald-800">NF-e Emitida (DANFE)</p>
                      <p className="text-sm text-emerald-600">
                        Nº {selectedOrder.nfe_number} | Chave: {selectedOrder.nfe_key?.slice(-10)}...
                      </p>
                    </div>
                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Cliente */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium text-slate-500">Cliente</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-1">
                <p className="font-semibold">{selectedOrder.customer_name}</p>
                <p className="text-sm text-slate-600">{selectedOrder.customer_phone}</p>
                {selectedOrder.customer_email && (
                  <p className="text-sm text-slate-600">{selectedOrder.customer_email}</p>
                )}
              </CardContent>
            </Card>
            
            {/* Entrega */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium text-slate-500">
                  {selectedOrder.delivery_type === 'entrega' ? 'Entrega' : 'Retirada'}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {selectedOrder.delivery_type === 'entrega' ? (
                  <p className="text-sm">{selectedOrder.delivery_address}</p>
                ) : (
                  <p className="text-sm text-slate-600">Cliente vai retirar no local</p>
                )}
              </CardContent>
            </Card>
            
            {/* Itens */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium text-slate-500">Itens do Pedido</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ScrollArea className="max-h-48">
                  <div className="space-y-2">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b last:border-0">
                        <div>
                          <p className="font-medium">{item.quantity}x {item.name}</p>
                          <p className="text-sm text-slate-500">{formatCurrency(item.price)} cada</p>
                        </div>
                        <p className="font-semibold">{formatCurrency(item.subtotal)}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
            
            {/* Totais */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span>{formatCurrency(selectedOrder.subtotal)}</span>
              </div>
              {selectedOrder.delivery_fee > 0 && (
                <div className="flex justify-between text-sm">
                  <span>Taxa de entrega</span>
                  <span>{formatCurrency(selectedOrder.delivery_fee)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total</span>
                <span className="text-emerald-600">{formatCurrency(selectedOrder.total)}</span>
              </div>
              <div className="flex justify-between text-sm text-slate-600">
                <span>Pagamento</span>
                <span className="capitalize">{selectedOrder.payment_method}</span>
              </div>
            </div>
            
            {/* Observações */}
            {selectedOrder.notes && (
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm font-medium text-slate-500">Observações</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm">{selectedOrder.notes}</p>
                </CardContent>
              </Card>
            )}
            
            {/* Ações */}
            <div className="flex justify-end gap-2 pt-4">
              {!selectedOrder.nfe_key && !selectedOrder.nfce_key && selectedOrder.status !== 'cancelled' && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedOrderNota(selectedOrder);
                      setTipoDocumento('nfce');
                      setShowEmitirNotaModal(true);
                    }}
                    disabled={emitindoNfce === selectedOrder.id}
                    className="text-green-600 border-green-300"
                  >
                    {emitindoNfce === selectedOrder.id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Receipt className="w-4 h-4 mr-2" />
                    )}
                    NFC-e
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedOrderNota(selectedOrder);
                      setTipoDocumento('nfe');
                      setShowEmitirNotaModal(true);
                    }}
                    disabled={emitindoNfce === selectedOrder.id}
                    className="text-blue-600 border-blue-300"
                  >
                    {emitindoNfce === selectedOrder.id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4 mr-2" />
                    )}
                    NF-e
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                onClick={() => sendWhatsApp(selectedOrder)}
                className="text-emerald-600"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Enviar WhatsApp
              </Button>
              <Button variant="outline" onClick={() => setSelectedOrder(null)}>
                Fechar
              </Button>
            </div>
          </div>
        )}
      </SimpleModal>

      {/* Modal Emitir Nota Fiscal */}
      {showEmitirNotaModal && selectedOrderNota && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowEmitirNotaModal(false); setSelectedOrderNota(null); }}>
          <div 
            className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-slate-900 mb-2">Emitir Documento Fiscal</h2>
            <p className="text-slate-600 mb-2">Pedido #{selectedOrderNota.order_number}</p>
            <p className="text-lg font-semibold text-emerald-600 mb-6">{formatCurrency(selectedOrderNota.total)}</p>
            
            <div className="space-y-3 mb-6">
              <button
                type="button"
                onClick={() => setTipoDocumento('nfce')}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  tipoDocumento === 'nfce' 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Receipt className="w-5 h-5 text-green-600" />
                  <div>
                    <span className="font-semibold block">NFC-e - Nota Fiscal do Consumidor</span>
                    <span className="text-sm text-slate-500">Para vendas a consumidor final</span>
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setTipoDocumento('nfe')}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  tipoDocumento === 'nfe' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <div>
                    <span className="font-semibold block">NF-e - Nota Fiscal Eletrônica</span>
                    <span className="text-sm text-slate-500">Para vendas entre empresas (B2B)</span>
                  </div>
                </div>
              </button>
            </div>
            
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { setShowEmitirNotaModal(false); setSelectedOrderNota(null); }} className="flex-1">
                Cancelar
              </Button>
              <Button 
                onClick={handleEmitirNota} 
                disabled={emitindoNfce === selectedOrderNota?.id}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {emitindoNfce === selectedOrderNota?.id ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  tipoDocumento === 'nfce' ? <Receipt className="w-4 h-4 mr-2" /> : <FileText className="w-4 h-4 mr-2" />
                )}
                {emitindoNfce === selectedOrderNota?.id ? 'Emitindo...' : `Emitir ${tipoDocumento === 'nfe' ? 'NF-e' : 'NFC-e'}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PedidosOnlinePage;
